//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

import { flexRender, useReactTable } from '@tanstack/react-table';
import { ColumnDef, getCoreRowModel, getFilteredRowModel, getSortedRowModel } from '@tanstack/table-core';
import CheckCircle from 'assets/check-circle.svg';
import ErrorCircle from 'assets/exclamation-x-circle.svg';
import { ReactComponent as Star } from 'assets/yellow-star.svg';
import EmptyTable from 'components/EmptyTable';
import SearchInput from 'components/SearchInput';
import TableHeader from 'components/TableHeader';
import { useAuth } from 'contexts/AuthProvider';
import { useToast } from 'contexts/ToastProvider';
import { User } from 'entities/user.entity';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { containsFilter, getRoleText, mapMemberStatusColor, mapMemberStatusString } from 'utils/helper';
import { Role } from '..';
import EditMemberDrawer from './EditMemberDrawer';

interface MembersType {
  showDialog: boolean;
  setShowDialog: Dispatch<SetStateAction<boolean>>;
  requestConfirm: (action: () => void) => void;
}

interface EditMemberType {
  email: string;
  name: string;
  cloud: number;
  role: string;
}

const Members = ({ showDialog, setShowDialog, requestConfirm }: MembersType) => {
  const { t, i18n } = useTranslation();
  const { addToast } = useToast();
  const { getDeviceMembers, putUserRole } = useAuth();
  const [members, setMembers] = useState<User[]>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [userData, setUserData] = useState<User>({} as User);
  const methods = useForm<EditMemberType>();

  useEffect(() => {
    setOpenDrawer(false);
    getMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = useCallback(() => {
    setOpenDrawer(false);
    methods.reset();
  }, [methods]);

  const handleDrawerClose = useCallback(() => {
    if (!showDialog) {
      resetForm();
      return;
    }
    requestConfirm(() => {
      resetForm();
    });
  }, [showDialog, requestConfirm, resetForm]);

  useEffect(() => {
    setShowDialog(methods.formState.isDirty && !methods.formState.isSubmitSuccessful);
  }, [methods.formState.isDirty, methods.formState.isSubmitSuccessful]);

  const getMembers = async () => {
    try {
      const { data } = await getDeviceMembers();
      setMembers(data.filter((d) => d.role !== 'root'));
    } catch (error) {
      addToast({
        title: t('toast.getTableDataFailed'),
        message: '',
        color: 'bg-toast-error-50',
        icon: ErrorCircle,
        text: 'text-toast-error-800',
        svg: 'toastIcon-error',
      });
    }
  };

  const onSubmit = (data: EditMemberType) => {
    putUserRole(userData.id, { role: data.role })
      .then(() => {
        setOpenDrawer(false);
        getMembers();
        addToast({
          title: t('toast.updatedSuccessfully'),
          message: '',
          color: 'bg-toast-success-50',
          icon: CheckCircle,
          text: 'text-toast-success-800',
          svg: 'toastIcon-success',
        });
      })
      .catch((error) => {
        addToast({
          title: t('toast.updatedFailed'),
          message: error.response.data.message ?? '',
          color: 'bg-toast-error-50',
          icon: ErrorCircle,
          text: 'text-toast-error-800',
          svg: 'toastIcon-error',
        });
      });
  };

  const onClickEdit = useCallback((data: User) => {
    setOpenDrawer(true);
    setUserData(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = useMemo<ColumnDef<User, any>[]>(
    () => [
      {
        accessorKey: 'name',
        header: t('profile.tableName'),
        className: 'uppercase',
        cell: (props) => (
          <div className="flex justify-center items-center whitespace-pre-wrap break-all text-center">
            <span>{props.getValue() === '' ? '--' : props.getValue()}</span>
            {props.row.original.role === Role.Owner && <Star className="ml-2 flex-shrink-0" />}
          </div>
        ),
      },
      {
        accessorKey: 'email',
        header: t('profile.tableEmail'),
        className: 'uppercase',
        cell: (props) => <div className="break-all">{props.getValue()}</div>,
      },
      {
        accessorKey: 'isActive',
        header: t('profile.tableStatus'),
        className: 'uppercase',
        cell: (props) => {
          const status = props.getValue() ? 'active' : 'pending';
          return <span className={`capitalize ${mapMemberStatusColor(status)}`}>{mapMemberStatusString(status)}</span>;
        },
      },
      {
        accessorKey: 'role',
        header: t('profile.tableRole'),
        className: 'uppercase',
        cell: (props) => <span className="capitalize">{getRoleText(props.getValue())}</span>,
      },
      {
        accessorKey: 'actions',
        header: t('profile.tableActions'),
        enableSorting: false,
        className: 'uppercase',
        cell: (props) =>
          props.row.original.role !== Role.Owner ? (
            <span
              className="text-blue-500 cursor-pointer"
              onClick={() => onClickEdit(props.row.original)}
              data-testid={`click-to-edit-member-for-rowid-${props.row.id}`}
            >
              {t('profile.tableEdit')}
            </span>
          ) : (
            <>--</>
          ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onClickEdit, i18n.language]
  );

  const table = useReactTable({
    data: members,
    columns,
    filterFns: { contains: containsFilter },
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: containsFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });
  return (
    <div className="drawer drawer-end flex-col h-full">
      <input id="addMemberDrawer" type="checkbox" className="drawer-toggle" checked={openDrawer} readOnly />
      <div className="drawer-content">
        <div className="flex pl-4 justify-end py-2">
          <div className="float-right pr-4">
            <SearchInput
              value={globalFilter ?? ''}
              onChange={(value) => setGlobalFilter(String(value))}
              className="input border-0 px-4 py-1 input-ghost"
              placeholder={t('component.searchInput')}
            />
          </div>
        </div>
        <div className="card-body setting">
          <div className="pt-2 px-4">
            <div className="table-overflow">
              <table className="table w-full text-center" data-testid="profile-members-table">
                <TableHeader table={table} />
                <tbody>
                  {table.getRowModel().rows.length > 0 ? (
                    table.getRowModel().rows.map((row) => {
                      return (
                        <tr key={row.id} className="hover">
                          {row.getVisibleCells().map((cell) => (
                            <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                          ))}
                        </tr>
                      );
                    })
                  ) : (
                    <EmptyTable columnLength={table.getVisibleLeafColumns().length} />
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div className="drawer-side" onClick={handleDrawerClose}>
        <label htmlFor="addMemberDrawer" className="drawer-overlay"></label>
        <div
          className="w-2/5 bg-base-100 overflow-y-auto"
          onClick={(event) => {
            // 防止觸發外層 click event(setOpenDrawer)
            event.stopPropagation();
          }}
        >
          <FormProvider {...methods}>
            <form className="flex flex-col h-full" onSubmit={methods.handleSubmit(onSubmit)}>
              <EditMemberDrawer userData={userData} handleDrawerClose={handleDrawerClose} />
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  );
};

export default Members;
